import { codeFrame } from '@rehaje/shared'
import { Parser } from 'acorn'
import jsx from 'acorn-jsx'

import type {
  AnyNode,
  Expression,
  Identifier,
  Options,
  Program,
  SourceLocation
} from 'acorn'
/**
 * 为 acorn AST 注入 Identifier.subchain / Identifier.invoked 信息
 *
 * 行为与 Mustache 模块中的 acorn 插件保持一致：
 * - 对于 MemberExpression：
 *   root.subchain = 源码中从 root 结束位置到整个 MemberExpression 结束位置的切片
 *   例如：query1.data.map -> root = Identifier(query1), subchain = '.data.map'
 * - 对于以 MemberExpression 为 callee 的 CallExpression：
 *   root.subchain = 去掉最后一个方法名后的路径
 *   例如：query1.data.map() -> subchain = '.data'
 * - 对于调用表达式：
 *   - 直接调用：`query1()` => callee.invoked = true
 *   - 成员调用：`query1.run()` => 根标识符 `query1.invoked = true`
 */
const SubchainPlugin = (BaseParser: typeof Parser) => {
  return class SubchainParser extends BaseParser {
    override parseSubscript(
      base: AnyNode,
      startPos: number,
      startLoc?: SourceLocation,
      noCalls?: boolean,

      ...args: any[]
    ): Expression {
      const node = super.parseSubscript(
        base,
        startPos,
        startLoc,
        noCalls,
        ...args
      ) as AnyNode

      // MemberExpression: 记录从根 Identifier 到当前节点的完整属性链
      if (node.type === 'MemberExpression') {
        let root: AnyNode = node.object
        while (root.type === 'MemberExpression') {
          root = root.object
        }
        if (root.type === 'Identifier') {
          ;(root as Identifier).subchain = this.input.slice(root.end, node.end)
        }
      }

      // CallExpression：标记调用根并（在成员调用场景下）计算 subchain
      if (node.type === 'CallExpression') {
        // 直接调用：query1()
        if (node.callee.type === 'Identifier') {
          ;(node.callee as Identifier).invoked = true
        }

        // 成员调用：query1.run()
        if (node.callee.type === 'MemberExpression') {
          let root: AnyNode = node.callee.object
          while (root.type === 'MemberExpression') {
            root = root.object
          }
          if (root.type === 'Identifier') {
            root.invoked = true
            const fullPath = this.input.slice(root.end, node.callee.end)
            const lastDotIndex = fullPath.lastIndexOf('.')
            root.subchain = fullPath.slice(0, Math.max(0, lastDotIndex))
            root.invokedMethod = fullPath.slice(lastDotIndex + 1)
          }
        }
      }

      return node as Expression
    }
  }
}

const JSXParser = Parser.extend(jsx() as any, SubchainPlugin as any)

export function parse(code = '', options?: Partial<Options>): Program {
  try {
    return JSXParser.parse(code, {
      allowAwaitOutsideFunction: true,
      allowHashBang: true,
      allowImportExportEverywhere: true,
      allowReserved: true,
      allowReturnOutsideFunction: true,
      allowSuperOutsideMethod: true,
      ecmaVersion: 'latest',
      jsx: true,
      locations: true,
      ranges: true,
      sourceType: 'module',
      ...options
    } as Options) as Program
  } catch (error) {
    if (error instanceof SyntaxError) {
      const frame = codeFrame(code, error.loc.line, error.loc.column)
      error.message = `${error.message}\n\n${frame}\n`
    }
    throw error
  }
}
